import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, formValueSelector, change } from 'redux-form';
import { SelectField } from 'redux-form-material-ui';
import { MenuItem, Dialog, DatePicker, SelectField as SimpleSelect, RaisedButton, Divider } from 'material-ui';
import { compose, getContext } from 'recompose';
import _ from 'lodash';
import { getEntities, eventsKeyBySportAndTtId, eventsKeyByDate } from 'dash-front/selectors';
import { fetchEventsByTournament, fetchEventsByDate, fetchEventById } from 'dash-front/actions';
import { getFieldPath } from 'dash-front/campaign/components/helpers';

const getEventLabel = (events, id) => {
    const event = _.get(events, id);
    return _.isObject(event) ? `${event.homeName} - ${event.awayName}` : `EventId: ${id}`;
};

const alphabetSort = (a, b) => {
    var A = a.toLowerCase();
    var B = b.toLowerCase();
    if (A < B) {
        return -1;
    }
    return A > B ? 1 : 0;
};

const groupLabelStyle = {
    padding: '5px 24px',
    fontSize: '12px',
    backgroundColor: '#eaeaea'
};
const GroupLabel = ({ label }) => <div style={groupLabelStyle}>{label}</div>;

const EventPickerByDate = connect((state, { date }) => ({
    events: getEntities(state).events,
    eventIds: date ? getEntities(state)[eventsKeyByDate(date)] : null
}))(({ date, eventIds, events, onChange }) => {
    const eventsAr = (Array.isArray(eventIds) ? eventIds : []).map(id =>
        _.get(events, id, { eventId: id, tournamentName: 'N/A' })
    );
    const grouped = _.groupBy(eventsAr, 'tournamentName');
    const tournaments = Object.keys(grouped).sort(alphabetSort);
    return (
        <div>
            {!Array.isArray(eventIds) ? null : (
                <SimpleSelect onChange={onChange} value={null} floatingLabelText={'Event'} style={{ width: 420 }}>
                    {tournaments.map(tournament => {
                        const ids = grouped[tournament].map(e => e.eventId);
                        return [<GroupLabel key={tournament} label={tournament} />].concat(
                            ids.map(id => (
                                <MenuItem key={id} value={String(id)} primaryText={getEventLabel(events, id)} />
                            ))
                        );
                    })}
                </SimpleSelect>
            )}
        </div>
    );
});

class EventPicker extends React.PureComponent {
    componentDidMount() {
        const { sport, tournamentId, updateEvents, value, events } = this.props;
        if (value && value !== '*' && !_.get(events, value)) {
            this.props.fetchEventById(value);
        }
        updateEvents(sport, tournamentId);
    }

    componentWillReceiveProps({ sport, tournamentId }) {
        if (this.props.sport !== sport || tournamentId !== this.props.tournamentId) {
            this.props.updateEvents(sport, tournamentId);
        }
    }

    state = { dialogOpen: false, date: null };

    openDialog = () => {
        this.setState({ dialogOpen: true });
    };

    closeDialog = () => {
        this.setState({ dialogOpen: false });
    };

    onDateChange = (jsEvent, date) => {
        this.setState({ date });
        this.props.updateEventsByDate(date);
    };

    onEventByDateChange = (jsEvent, index, eventId) => {
        const { name, _reduxForm: { sectionPrefix } } = this.props;
        const path = getFieldPath(sectionPrefix, name);
        this.props.manualChange(path, eventId);
        this.closeDialog();
    };

    renderOptions() {
        const { eventIds, value } = this.props;
        const { events } = this.props;
        const ids = Array.isArray(eventIds) ? eventIds.slice() : [];
        if (value && value !== '*' && !ids.some(id => String(id) === String(value))) {
            ids.push(value);
        }
        const eventsAr = ids.map(id => _.get(events, id, { eventId: id, tournamentName: 'N/A' }));
        const grouped = _.groupBy(eventsAr, 'tournamentName');
        return Object.keys(grouped).map(tournament => {
            const tournIds = grouped[tournament].map(el => el.eventId);
            return [<GroupLabel key={tournament} label={tournament} />].concat(
                tournIds.map(id => <MenuItem key={id} value={String(id)} primaryText={getEventLabel(events, id)} />)
            );
        });
    }

    render() {
        const { name } = this.props;
        const fieldProps = _.omit(this.props, Object.keys(EventPicker.propTypes));
        return (
            <div>
                <Field floatingLabelText={'Event (match)'} {...fieldProps} name={name} component={SelectField}>
                    <MenuItem value={'*'} primaryText={'Any'} />
                    {this.renderOptions()}
                    <Divider />
                    <MenuItem primaryText={'Find a match'} onTouchTap={this.openDialog} />
                </Field>
                <Dialog
                    open={this.state.dialogOpen}
                    onRequestClose={this.closeDialog}
                    contentStyle={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: 24
                    }}
                    actions={[<RaisedButton secondary label={'Close'} onTouchTap={this.closeDialog} />]}
                >
                    <DatePicker
                        value={this.state.date}
                        floatingLabelText={'Event date'}
                        onChange={this.onDateChange}
                        textFieldStyle={{ width: 420 }}
                    />
                    <EventPickerByDate date={this.state.date} onChange={this.onEventByDateChange} />
                </Dialog>
            </div>
        );
    }
}

EventPicker.propTypes = {
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sport: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tournamentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eventIds: PropTypes.array,
    events: PropTypes.object,
    updateEvents: PropTypes.func.isRequired,
    updateEventsByDate: PropTypes.func.isRequired,
    fetchEventById: PropTypes.func.isRequired,
    manualChange: PropTypes.func.isRequired,
    _reduxForm: PropTypes.object
};

const mapStateToProps = (state, { sport, tournamentId, name, _reduxForm: { form, sectionPrefix } }) => {
    const entities = getEntities(state);
    const eventsKey = eventsKeyBySportAndTtId(sport, tournamentId);
    const valuePath = getFieldPath(sectionPrefix, name);
    return {
        eventIds: entities[eventsKey],
        events: entities.events,
        value: formValueSelector(form)(state, valuePath)
    };
};

const mapDispatchToProps = (dispatch, { _reduxForm: { form } }) => ({
    updateEvents: (sportId, tournamentId) => dispatch(fetchEventsByTournament({ sportId, tournamentId })),
    updateEventsByDate: date => dispatch(fetchEventsByDate(date)),
    manualChange: (path, eventId) => dispatch(change(form, path, eventId)),
    fetchEventById: id => dispatch(fetchEventById(id))
});

export default compose(
    getContext({
        _reduxForm: PropTypes.object
    }),
    connect(mapStateToProps, mapDispatchToProps)
)(EventPicker);



/** WEBPACK FOOTER **
 ** ../src/dash/front/campaign/components/Form/pickers/EventPicker2.js
 **/